/**
 * API client: HTTP + WebSocket pro plx-fluxo backend.
 *
 * Autenticação é transparente: Cloudflare Access injeta JWT no cookie
 * CF_Authorization antes das requisições chegarem no Worker. `credentials:
 * 'include'` garante que o cookie vai junto.
 *
 * Uso:
 *   const { plan, nodes, edges } = await api.getDefaultPlan();
 *   const ws = api.connectPlan(plan.id, {
 *     onSnapshot: ({nodes, edges, presence}) => {...},
 *     onNodeUpsert: (node) => {...},
 *     onNodeDelete: (id) => {...},
 *     onEdgeUpsert: (edge) => {...},
 *     onEdgeDelete: (id) => {...},
 *     onCursor: ({user_id, x, y}) => {...},
 *     onPresence: (users) => {...},
 *   });
 *   ws.send({ t: 'node.upsert', node });
 */

const base = (p) => (import.meta.env.DEV ? '' : '') + p;

async function req(path, opts = {}) {
  const res = await fetch(base(path), {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${detail}`);
  }
  return res.json();
}

export const api = {
  me: () => req('/api/me'),
  loginRequest: (email) => req('/api/login/request', { method: 'POST', body: JSON.stringify({ email }) }),
  loginVerify: (email, code) => req('/api/login/verify', { method: 'POST', body: JSON.stringify({ email, code }) }),
  logout: () => req('/api/logout', { method: 'POST' }),
  getDefaultPlan: () => req('/api/plans/default'),
  getPlan: (id) => req(`/api/plans/${id}`),
  listPlans: () => req('/api/plans'),
  createPlan: (body) => req('/api/plans', { method: 'POST', body: JSON.stringify(body) }),
  updatePlan: (id, patch) => req(`/api/plans/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  updatePlanNorte: async (id, norte) => {
    // Norte é field do plano — precisaria endpoint PATCH. Por ora: re-query sempre.
    return req(`/api/plans/${id}`);
  },
  llm: (body) => req('/api/llm', { method: 'POST', body: JSON.stringify(body) }),

  connectPlan(planId, handlers) {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/api/plans/${planId}/ws`;
    const ws = new WebSocket(url);
    const queue = [];

    ws.addEventListener('open', () => {
      while (queue.length) ws.send(queue.shift());
    });
    ws.addEventListener('message', (ev) => {
      let m;
      try { m = JSON.parse(ev.data); } catch { return; }
      switch (m.t) {
        case 'snapshot':   handlers.onSnapshot?.(m); break;
        case 'node.upsert': handlers.onNodeUpsert?.(m.node); break;
        case 'node.delete': handlers.onNodeDelete?.(m.id); break;
        case 'edge.upsert': handlers.onEdgeUpsert?.(m.edge); break;
        case 'edge.delete': handlers.onEdgeDelete?.(m.id); break;
        case 'cursor':      handlers.onCursor?.(m); break;
        case 'presence':    handlers.onPresence?.(m.users); break;
      }
    });
    ws.addEventListener('close', () => handlers.onClose?.());
    ws.addEventListener('error', (e) => handlers.onError?.(e));

    return {
      send(msg) {
        const payload = JSON.stringify(msg);
        if (ws.readyState === WebSocket.OPEN) ws.send(payload);
        else queue.push(payload);
      },
      close: () => ws.close(),
      raw: ws,
    };
  },
};
